import { apiClient, unwrapApiResult } from '@/lib/openapi-client';
import type {
  BackgroundJobResponse,
  BriefSuggestionsRead,
  BriefValidationRead,
  CampaignAttentionResponse,
  CampaignLaunchMode,
  CampaignURLValidationResponse,
  SearchTermNarrativeResponse,
  SingleCampaignBuildRead,
  SingleCampaignCreate,
  SingleCampaignEligibilityRead,
  StrategyCreate,
  StrategyObjective,
  StrategyWizardSessionData,
  UpdateCampaignsRequest,
  UpdateMediaPlanRequest,
} from '@/types/generated';

const BASE = '/api/v1/strategies/wizard/{strategy_id}';

export const wizardApi = {
  getBriefSuggestions: (): Promise<BriefSuggestionsRead> =>
    unwrapApiResult(
      apiClient.GET('/api/v1/strategies/wizard/brief-suggestions'),
    ),

  getSingleCampaignEligibility: (
    objective: StrategyObjective,
  ): Promise<SingleCampaignEligibilityRead> =>
    unwrapApiResult(
      apiClient.GET('/api/v1/strategies/wizard/single-campaign/eligibility', {
        params: { query: { objective } },
      }),
    ),

  createSingleCampaign: (
    body: SingleCampaignCreate,
  ): Promise<SingleCampaignBuildRead> =>
    unwrapApiResult(
      apiClient.POST('/api/v1/strategies/wizard/single-campaign', { body }),
    ),

  // A pinned draft's brief edit rebuilds its one campaign; regenerateMediaPlan refuses it.
  rebuildSingleCampaign: (
    strategyId: number,
    body: SingleCampaignCreate,
  ): Promise<BackgroundJobResponse> =>
    unwrapApiResult(
      apiClient.POST(
        '/api/v1/strategies/wizard/{strategy_id}/single-campaign',
        {
          params: { path: { strategy_id: strategyId } },
          body,
        },
      ),
    ),

  validateBrief: (brief: StrategyCreate): Promise<BriefValidationRead> =>
    unwrapApiResult(
      apiClient.POST('/api/v1/strategies/wizard/validate-brief', {
        body: brief,
      }),
    ),

  getDraft: (strategyId: number): Promise<StrategyWizardSessionData> =>
    unwrapApiResult(
      apiClient.GET(BASE, { params: { path: { strategy_id: strategyId } } }),
    ),

  generateCampaigns: (strategyId: number): Promise<BackgroundJobResponse> =>
    unwrapApiResult(
      apiClient.POST('/api/v1/strategies/wizard/generate-campaigns', {
        body: { strategy_id: strategyId },
      }),
    ),

  updateCampaigns: (
    strategyId: number,
    body: UpdateCampaignsRequest,
  ): Promise<StrategyWizardSessionData> =>
    unwrapApiResult(
      apiClient.PATCH('/api/v1/strategies/wizard/{strategy_id}/campaigns', {
        params: { path: { strategy_id: strategyId } },
        // json2ts and openapi-typescript render this discriminated campaign union's
        // optional/required fields slightly differently (see unwrapApiResult's doc comment on
        // the response side of this same gap) — one boundary, asserted once here.
        body: body as never,
      }),
    ),

  updateMediaPlan: (
    strategyId: number,
    body: UpdateMediaPlanRequest,
  ): Promise<StrategyWizardSessionData> =>
    unwrapApiResult(
      apiClient.PATCH('/api/v1/strategies/wizard/{strategy_id}/media-plan', {
        params: { path: { strategy_id: strategyId } },
        body,
      }),
    ),

  regenerateCampaigns: (strategyId: number): Promise<BackgroundJobResponse> =>
    unwrapApiResult(
      apiClient.POST(
        '/api/v1/strategies/wizard/{strategy_id}/regenerate-campaigns',
        { params: { path: { strategy_id: strategyId } } },
      ),
    ),

  // brief omitted -> regenerate from the stored brief; provided -> persist the edit first.
  regenerateMediaPlan: (
    strategyId: number,
    brief?: StrategyCreate,
  ): Promise<BackgroundJobResponse> =>
    unwrapApiResult(
      apiClient.POST(
        '/api/v1/strategies/wizard/{strategy_id}/regenerate-media-plan',
        {
          params: { path: { strategy_id: strategyId } },
          body: brief,
        },
      ),
    ),

  pushCampaigns: (
    strategyId: number,
    launchMode: CampaignLaunchMode,
  ): Promise<BackgroundJobResponse> =>
    unwrapApiResult(
      apiClient.POST('/api/v1/strategies/wizard/push-campaigns', {
        body: { strategy_id: strategyId, launch_mode: launchMode },
      }),
    ),

  // Omit `urls` to validate the persisted draft; pass a list to validate the
  // editor's current (possibly unsaved) URLs.
  validateUrls: (
    strategyId: number,
    urls?: string[],
  ): Promise<CampaignURLValidationResponse> =>
    unwrapApiResult(
      apiClient.POST('/api/v1/strategies/wizard/{strategy_id}/validate-urls', {
        params: { path: { strategy_id: strategyId } },
        body: urls ? { urls } : undefined,
      }),
    ),

  getAttention: (strategyId: number): Promise<CampaignAttentionResponse> =>
    unwrapApiResult(
      apiClient.GET('/api/v1/strategies/wizard/{strategy_id}/attention', {
        params: { path: { strategy_id: strategyId } },
      }),
    ),

  getSearchTermNarrative: (
    strategyId: number,
  ): Promise<SearchTermNarrativeResponse> =>
    unwrapApiResult(
      apiClient.GET(
        '/api/v1/strategies/wizard/{strategy_id}/search-term-narrative',
        {
          params: { path: { strategy_id: strategyId } },
        },
      ),
    ),

  cancelDraft: async (strategyId: number): Promise<void> => {
    await unwrapApiResult(
      apiClient.DELETE(BASE, {
        params: { path: { strategy_id: strategyId } },
      }),
    );
  },
} as const;
